import { useContext, useCallback, useRef } from 'preact/hooks';
import { ChatContext } from '../hooks/context.js';
import { useChat } from '../hooks/useChat.js';
import { useSession } from '../hooks/useSession.js';
import {
  messages, isOpen, isLoading, showTyping, showHistory, showReview,
  suggestions, unreadCount, toastMessage, isMobile, isCompact, isMinimized,
  setOpen,
} from '../store/chatStore.js';
import { Header } from '../components/Header.jsx';
import { MessageList } from '../components/MessageList.jsx';
import { TypingIndicator } from '../components/TypingIndicator.jsx';
import { QuickReplies } from '../components/QuickReplies.jsx';
import { InputBox } from '../components/InputBox.jsx';
import { Footer } from '../components/Footer.jsx';
import { ReviewPrompt } from '../components/ReviewPrompt.jsx';
import { HistoryPanel } from '../components/HistoryPanel.jsx';
import { ICONS } from '../utils/constants.js';
import { MC } from '../utils/constants.js';

export function PopupLayout({ hostEl }) {
  const { cfg, bus, session } = useContext(ChatContext);
  const { send }              = useChat();
  const { startNewChat, loadSession } = useSession();
  const msgListRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (msgListRef.current) msgListRef.current.scrollTop = msgListRef.current.scrollHeight;
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    bus.emit('open', { sessionId: session.getOrCreate().sessionId });
    setTimeout(() => {
      const input = hostEl?.shadowRoot?.querySelector('.mc-input');
      input?.focus();
    }, 50);
  }, [bus, session, hostEl]);

  const handleToggle = useCallback(() => {
    isOpen.value ? handleClose() : handleOpen();
  }, [handleOpen]);

  const handleClose = useCallback(() => {
    showHistory.value = false;
    showReview.value  = false;
    setOpen(false);
    bus.emit('close', { sessionId: session.getOrCreate().sessionId });
  }, [bus, session]);

  const openStr  = isOpen.value;
  const mobStr   = isMobile.value;
  const cmpStr   = isCompact.value;
  const minStr   = isMinimized.value;
  const hasUnread = unreadCount.value > 0;

  return (
    <>
      {/* Launcher */}
      <button
        class="mc-launcher"
        aria-label={`Open ${cfg.botName}`}
        part="launcher"
        onClick={handleToggle}
      >
        <span class="mc-icon-chat" dangerouslySetInnerHTML={{ __html: ICONS.chat }} />
        <span class="mc-icon-close" dangerouslySetInnerHTML={{ __html: ICONS.close }} />
        {cfg.showNotificationBadge && (
          <span class="mc-badge" hidden={!hasUnread} aria-hidden="true" />
        )}
      </button>

      {/* Chat panel */}
      <div
        class="mc-panel"
        role="dialog"
        aria-label={cfg.botName}
        aria-modal="true"
      >
        <Header
          onNewChat={startNewChat}
          onHistory={() => { showHistory.value = !showHistory.value; }}
        />

        <MessageList messages={messages.value} isFloatpanel={false} />

        <TypingIndicator visible={showTyping.value} isFloatpanel={false} />

        <ReviewPrompt
          visible={showReview.value}
          isFloatpanel={false}
          onScrollBottom={scrollToBottom}
        />

        {suggestions.value.length > 0 && (
          <QuickReplies items={suggestions.value} onSend={send} isFloatpanel={false} />
        )}

        <InputBox onSend={send} isFloatpanel={false} />

        {cfg.showBranding && <Footer isFloatpanel={false} />}

        <HistoryPanel
          visible={showHistory.value}
          isFloatpanel={false}
          onLoadSession={loadSession}
          onClose={() => { showHistory.value = false; }}
        />

        {toastMessage.value && (
          <div class="mc-toast">{toastMessage.value}</div>
        )}
      </div>
    </>
  );
}
